// Client side of the inline proofreader. The heavy lifting (Amazon Nova
// Micro on Bedrock, the diff, and the police-vocabulary filters) lives in
// server/src/services/proofread; this module just batches paragraphs into
// requests and turns the server's spans into ProofSuggestion records.
import axios from "axios";
import type { ProofCategory, ProofSuggestion } from "./types";

interface ServerEdit {
  start: number;
  end: number;
  original: string;
  replacement: string;
  category: ProofCategory;
}

export interface ParagraphToLint {
  hash: string;
  text: string;
}

/** Server accepts at most this many paragraphs per request. */
export const MAX_PARAGRAPHS_PER_REQUEST = 3;

// Suggestions the officer dismissed this session, keyed by the exact change
// so the same slip retyped elsewhere stays quiet.
const ignored = new Set<string>();
const ignoreKey = (original: string, replacement: string) =>
  `${original} ${replacement}`;

export function ignoreSuggestion(s: ProofSuggestion): void {
  for (const r of s.replacements) ignored.add(ignoreKey(s.original, r));
}

/**
 * Lint up to three paragraphs in one round trip. Resolves to a map from
 * paragraph hash to suggestions; a paragraph the server couldn't process
 * simply maps to an empty list. Throws on transport errors so the caller
 * can back off.
 */
export async function lintParagraphs(
  paragraphs: ParagraphToLint[],
  signal?: AbortSignal,
): Promise<Map<string, ProofSuggestion[]>> {
  const { data } = await axios.post(
    "/api/proofread",
    { paragraphs: paragraphs.map((p) => ({ key: p.hash, text: p.text })) },
    { signal },
  );
  const out = new Map<string, ProofSuggestion[]>();
  const textByHash = new Map(paragraphs.map((p) => [p.hash, p.text]));
  for (const result of (data?.results || []) as {
    key: string;
    suggestions: ServerEdit[];
  }[]) {
    const text = textByHash.get(result.key);
    if (text === undefined) continue;
    const list: ProofSuggestion[] = [];
    for (const e of result.suggestions || []) {
      if (text.slice(e.start, e.end) !== e.original) continue;
      if (ignored.has(ignoreKey(e.original, e.replacement))) continue;
      list.push({
        id: `${result.key}:${e.start}:${e.end}:${e.category}`,
        blockHash: result.key,
        start: e.start,
        end: e.end,
        original: e.original,
        message: "",
        category: e.category,
        replacements: [e.replacement],
      });
    }
    out.set(result.key, list);
  }
  // Paragraphs missing from the response count as linted-and-clean.
  for (const p of paragraphs) if (!out.has(p.hash)) out.set(p.hash, []);
  return out;
}
